/**
 * FloatingPanel Component
 *
 * Shared floating panel for UI elements
 * Used for floating UI elements like settings bar, mobile menu, etc.
 *
 * Features:
 * - Multiple visual variants (glass with backdrop blur, opaque with solid background)
 * - Multiple position options (top-left, top-right, bottom-center)
 * - Responsive design (mobile-friendly)
 * - 100% CSS Modules (no inline styles)
 * - Dark/light mode support
 * - NO hover effects on panel itself (only on children)
 */

import type { ReactElement } from 'react';
import type { FloatingPanelProps } from './FloatingPanel.types';
import styles from './FloatingPanel.module.css';

export function FloatingPanel({
  children,
  position = 'top-right',
  variant = 'glass',
  className,
  'aria-label': ariaLabel,
}: FloatingPanelProps): ReactElement {
  const positionClass = {
    'top-left': styles.topLeft,
    'top-right': styles.topRight,
    'top-center': styles.topCenter,
    'bottom-center': styles.bottomCenter,
  }[position];

  const variantClass = variant === 'glass' ? styles.glass : styles.opaque;

  return (
    <div
      className={`${styles.floatingPanel} ${positionClass} ${variantClass} ${className || ''}`}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
}
